/**
 * Luftdruck → Höhe (docs/maengel-verortung-plan.md 9.1, Paket P2).
 *
 * Das Barometer taugt nur RELATIV: absolut liegt ein Handy-Sensor ±0,5–1 hPa
 * daneben (±4–8 m), und das Wetter verschiebt den Druck über den Tag um
 * 0,1–0,5 hPa/h. Deshalb rechnet hier alles gegen einen Bezugsdruck
 * (Nullpunkt der Begehung, geschoss.ts) — nie gegen die Normatmosphäre.
 *
 * Reine Funktionen, keine Abhängigkeit zu React/Capacitor. Nichts wirft:
 * unbrauchbare Werte (nicht endlich, Druck ≤ 0) ergeben `NaN` bzw. werden
 * übergangen.
 */

/** Temperatur der Luftsäule, wenn keine gemessen ist (Normatmosphäre, °C). */
export const STANDARD_TEMPERATUR_C = 15;

/** Spezifische Gaskonstante trockener Luft, J/(kg·K). */
const R_LUFT = 287.05;
/** Normfallbeschleunigung, m/s². */
const G_NORM = 9.80665;
/** 0 °C in Kelvin. */
const NULL_GRAD_K = 273.15;
/** MAD → Standardabweichung bei Normalverteilung. */
const MAD_FAKTOR = 1.4826;

/** Standardfenster für den Median: 10 s filtern Windböen und Türenschlagen. */
const FENSTER_MS = 10_000;
/** So lange hält der Puffer Proben vor. */
const MAX_ALTER_MS = 60_000;

function temperaturK(temperaturC: number | undefined): number {
    const c = typeof temperaturC === 'number' && Number.isFinite(temperaturC) ? temperaturC : STANDARD_TEMPERATUR_C;
    const k = c + NULL_GRAD_K;
    // Unter dem absoluten Nullpunkt gibt es keine Luftsäule — dann Normwert.
    return k > 0 ? k : STANDARD_TEMPERATUR_C + NULL_GRAD_K;
}

function druckGueltig(hPa: number): boolean {
    return Number.isFinite(hPa) && hPa > 0;
}

/**
 * Höhenunterschied in m von pRef nach p (positiv = höher).
 * Hypsometrisch: (R·T/g)·ln(pRef/p), R = 287,05, g = 9,80665,
 * T = temperaturC + 273,15 (Standard 15 °C).
 *
 * Faustwert: 1 hPa ≈ 8,4 m, ein Geschoss (3 m) ≈ 0,36 hPa. Die Temperatur
 * wirkt nur proportional (10 K ≈ 3,5 %), bei einem Geschoss also wenige cm.
 * Ungültiger Druck (≤ 0, nicht endlich) → `NaN`.
 */
export function hoehenDifferenzM(pRefHpa: number, pHpa: number, temperaturC?: number): number {
    if (!druckGueltig(pRefHpa) || !druckGueltig(pHpa)) return Number.NaN;
    return ((R_LUFT * temperaturK(temperaturC)) / G_NORM) * Math.log(pRefHpa / pHpa);
}

/** Eine Druckmessung des Sensors: hPa und Zeitpunkt in Unix-Millisekunden. */
export interface DruckProbe {
    hPa: number;
    t: number;
}

/** Median einer bereits sortierten, nicht leeren Liste. */
function medianSortiert(werte: readonly number[]): number {
    const mitte = Math.floor(werte.length / 2);
    return werte.length % 2 === 1 ? werte[mitte] : (werte[mitte - 1] + werte[mitte]) / 2;
}

/** Median einer beliebigen Liste; `NaN` bei leerer Liste. */
export function median(werte: readonly number[]): number {
    if (werte.length === 0) return Number.NaN;
    return medianSortiert([...werte].sort((x, y) => x - y));
}

/**
 * Median der Proben mit t in [jetzt − fensterMs, jetzt] (Standard 10 s);
 * streuungHpa = MAD · 1,4826 (robuste Standardabweichung, ein einzelner
 * Ausreißer verschiebt sie nicht). `null` ohne verwertbare Proben.
 */
export function druckMedian(
    proben: readonly DruckProbe[],
    jetzt: number,
    fensterMs: number = FENSTER_MS,
): { hPa: number; anzahl: number; streuungHpa: number } | null {
    const fenster = Number.isFinite(fensterMs) && fensterMs >= 0 ? fensterMs : FENSTER_MS;
    const ab = jetzt - fenster;
    const werte: number[] = [];
    for (const p of proben) {
        if (!p || !druckGueltig(p.hPa) || !Number.isFinite(p.t)) continue;
        if (p.t < ab || p.t > jetzt) continue;
        werte.push(p.hPa);
    }
    if (werte.length === 0) return null;
    werte.sort((x, y) => x - y);
    const m = medianSortiert(werte);
    const abweichungen = werte.map((w) => Math.abs(w - m)).sort((x, y) => x - y);
    return { hPa: m, anzahl: werte.length, streuungHpa: medianSortiert(abweichungen) * MAD_FAKTOR };
}

/**
 * Neue Liste nur mit Proben der letzten maxAlterMs (Standard 60 s) — hält den
 * Puffer im Messmodus klein. Proben mit Zeitstempel NACH `jetzt` (Uhren von
 * Sensor und Seite weichen minimal ab) bleiben drin; Reihenfolge bleibt.
 */
export function probenKappen(proben: readonly DruckProbe[], jetzt: number, maxAlterMs: number = MAX_ALTER_MS): DruckProbe[] {
    const maxAlter = Number.isFinite(maxAlterMs) && maxAlterMs >= 0 ? maxAlterMs : MAX_ALTER_MS;
    const ab = jetzt - maxAlter;
    return proben.filter((p) => !!p && Number.isFinite(p.t) && p.t >= ab);
}
